import React, { useState, useEffect } from "react";
import {
  Typography,
  Stack,
  Chip,
  Box,
  Skeleton,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import { useSelector } from "react-redux";
import { db } from "config/firebase";
import { doc, getDoc } from "firebase/firestore";
import { RootState } from "types";

import UpdatePhoneNumber from "./UpdatePhoneNumber.jsx";

const InfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'transparent',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 500,
  minWidth: '120px',
}));

interface UserData {
  joinDate: string;
  displayName?: string;
  email?: string;
  phoneNumber?: string;
  [key: string]: any;
}

interface UserInfoProps {
  userId: string;
}

function UserInfo({ userId }: UserInfoProps) {
  const user = useSelector((state: RootState) => state.user.user);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getUserData();
  }, [userId]);

  const getUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const userRef = doc(db, "users", userId);
      const docSnap = await getDoc(userRef);

      if (docSnap.exists()) {
        setUserData(docSnap.data() as UserData);
      } else {
        setError("User information not found.");
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
      setError("Failed to load user information.");
    } finally {
      setLoading(false);
    }
  };

  const formatJoinDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          User Information
        </Typography>
        <Stack spacing={2}>
          <Skeleton variant="text" height={40} />
          <Skeleton variant="text" height={40} />
          <Skeleton variant="text" height={40} />
          <Skeleton variant="text" height={40} />
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          User Information
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!userData) {
    return null;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon color="primary" />
        User Information
      </Typography>
      
      <Stack spacing={1}>
        <InfoItem>
          <PersonIcon color="action" />
          <InfoLabel variant="body2">Display Name:</InfoLabel>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {user.displayName || 'Not set'}
          </Typography>
        </InfoItem>

        <InfoItem>
          <EmailIcon color="action" />
          <InfoLabel variant="body2">Email:</InfoLabel>
          <Typography variant="body1">{user.email}</Typography>
        </InfoItem>
        
        <InfoItem>
          <PhoneIcon color="action" />
          <InfoLabel variant="body2">Phone:</InfoLabel>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {user.phoneNumber ? (
              <Chip 
                label={user.phoneNumber} 
                variant="outlined" 
                size="small" 
                color="success"
              />
            ) : (
              <Chip 
                label="Not verified" 
                variant="outlined" 
                size="small" 
                color="warning"
              />
            )}
            <UpdatePhoneNumber />
          </Box>
        </InfoItem>
        
        <InfoItem>
          <CalendarTodayIcon color="action" />
          <InfoLabel variant="body2">Member Since:</InfoLabel>
          <Typography variant="body1">
            {formatJoinDate(userData.joinDate)}
          </Typography>
        </InfoItem>
      </Stack>
    </Box>
  );

}

export default UserInfo;
